/**
 * Pages Location matériel — catalogue (tranche 1).
 *
 * Exporte :
 *   - MaterielCataloguePage : liste/grille du catalogue, filtres catégorie + recherche
 *   - MaterielFormPage      : création / édition d'un matériel
 *
 * Module "Service technique" > Location de matériel. Tranche 1 : gestion du
 * catalogue côté staff (manager/admin). Les réservations administré arrivent
 * en tranche 2.
 *
 * Design aligné R8 service-technique : topbar glass halo tech, sub-nav pills
 * (5 onglets : Planning / Fiches incident / Arrêtés / Véhicules / Matériel),
 * cards glass. R13 : aucun window.alert/confirm — useConfirmDialog.
 */

import { ServiceTechniqueNav } from '@/components/ServiceTechniqueNav';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { FichePhotos } from '@/components/FichePhotos';
import { LieuSelect } from '@/components/LieuSelect';
import {
  Button, Badge, Card, Alert, Field, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import {
  Boxes, PartyPopper, Armchair, ShieldAlert, Package, DoorOpen,
  Landmark, ArrowLeft, Plus, Save, Trash2, Search, AlertCircle, Euro,
  CalendarDays, Inbox, Check, X, ClipboardList, User, Wrench, KeyRound,
} from 'lucide-react';
import { ClesRemiseSelect } from '@/components/ClesRemiseSelect';
import { EtatDesLieuxResume } from '@/components/EtatDesLieuxResume';
import { DeclarationManifestation } from '@/components/DeclarationManifestation';
import type {
  MaterielCatalogue, MaterielCategorie,
  MaterielReservation, MaterielReservationStatut,
} from '@roronoa-collectivites/shared-types';
import { RESERVATION_STATUT } from './MaterielReservationPages';

// =============================================================================
// Helpers
// =============================================================================

const CATEGORIE_LABEL: Record<MaterielCategorie, string> = {
  evenement: 'Événement',
  mobilier:  'Mobilier',
  securite:  'Sécurité',
  outils:    'Outils',
  salle:     'Salle',
  autre:     'Autre',
};

const CATEGORIE_ICON: Record<MaterielCategorie, typeof Package> = {
  evenement: PartyPopper,
  mobilier:  Armchair,
  securite:  ShieldAlert,
  outils:    Wrench,
  salle:     DoorOpen,
  autre:     Package,
};

const CATEGORIES: MaterielCategorie[] = ['mobilier', 'securite', 'outils', 'salle', 'autre'];

const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};
const SUBNAV_BG = {
  backgroundImage: 'linear-gradient(180deg, oklch(var(--c-bg-2) / 0.3), oklch(var(--c-bg) / 0.4))',
};
const INPUT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';
const TEXTAREA_CLS = 'w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

function formatEuro(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// =============================================================================
// Layout (topbar + sub-nav Service technique)
// =============================================================================

function MaterielLayout({
  title, eyebrow, icon, action, section, children,
}: {
  title: string;
  eyebrow: string;
  icon: typeof Boxes;
  action?: React.ReactNode;
  section?: 'catalogue' | 'reservations';
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Service technique" accent="tech" />

      {/* D-09 — barre PARTAGÉE. Ces pages réimplémentaient leur propre barre à
          5 onglets : « États des lieux » et « Sécurité ERP » disparaissaient donc
          dès qu'on venait ici. Une seule source de vérité pour la sous-nav. */}
      <ServiceTechniqueNav active="materiel" right={action} />

      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5 animate-fade-in">
        <div className="mb-5 flex items-center gap-3">
          <LogoMark icon={icon} accent="tech" size="lg" />
          <div>
            <SectionEyebrow as="div">{eyebrow}</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              {title}
            </h1>
          </div>
        </div>

        {section && (
          <div className="mb-4 flex flex-wrap gap-2">
            <SectionPill active={section === 'catalogue'} onClick={() => navigate('/materiel')}>
              <Boxes className="h-3.5 w-3.5" aria-hidden="true" /> Catalogue
            </SectionPill>
            <SectionPill active={section === 'reservations'} onClick={() => navigate('/materiel/reservations')}>
              <ClipboardList className="h-3.5 w-3.5" aria-hidden="true" /> Réservations
            </SectionPill>
          </div>
        )}

        {children}
      </main>
    </div>
  );
}

function MatTab({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  if (active) {
    return (
      <span className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-tech/30 bg-tech-tint/80 px-3 text-xs font-medium text-tech-fg backdrop-blur-glass">
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-transparent bg-transparent px-3 text-xs font-medium text-ink-muted backdrop-blur-glass transition-all hover:border-border hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      {children}
    </button>
  );
}

// =============================================================================
// CATALOGUE
// =============================================================================

export function MaterielCataloguePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<MaterielCatalogue[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState<MaterielCategorie | 'tout'>('tout');

  const canCreate = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');

  async function reload() {
    setLoading(true); setError(null);
    try { setItems(await api.materiel.list()); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void reload(); }, []);

  const filtered = (items ?? []).filter((m) => {
    if (filterCat !== 'tout' && m.categorie !== filterCat) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!m.nom.toLowerCase().includes(s)
          && !(m.description?.toLowerCase().includes(s) ?? false)) return false;
    }
    return true;
  });

  return (
    <MaterielLayout
      title="Catalogue des locations"
      eyebrow="Location de matériel"
      icon={Boxes}
      section="catalogue"
      action={canCreate ? (
        <Button variant="primary" size="sm" onClick={() => navigate('/materiel/nouveau')}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Ajouter du matériel</span>
        </Button>
      ) : undefined}
    >
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>
      )}

      {/* Filtres catégorie + recherche */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <CatFilterPill active={filterCat === 'tout'} onClick={() => setFilterCat('tout')}>
          Tout
        </CatFilterPill>
        {CATEGORIES.map((c) => (
          <CatFilterPill key={c} active={filterCat === c} onClick={() => setFilterCat(c)}>
            {CATEGORIE_LABEL[c]}
          </CatFilterPill>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un matériel…"
            className={INPUT_CLS + ' pl-9'}
          />
        </div>
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-ink-subtle">Chargement…</p>
      ) : filtered.length === 0 ? (
        <Card className="py-12 text-center">
          <Boxes className="mx-auto h-10 w-10 text-ink-dim" aria-hidden="true" />
          <p className="mt-3 text-sm text-ink-muted">
            {items && items.length === 0
              ? 'Aucun matériel dans le catalogue pour le moment.'
              : 'Aucun matériel ne correspond à ces filtres.'}
          </p>
          {canCreate && items && items.length === 0 && (
            <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate('/materiel/nouveau')}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Ajouter le premier matériel
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((m) => (
            <MaterielCard
              key={m.id}
              m={m}
              onClick={() => canCreate && navigate(`/materiel/${m.id}/edit`)}
              clickable={canCreate}
            />
          ))}
        </div>
      )}
    </MaterielLayout>
  );
}

function CatFilterPill({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex h-8 items-center rounded-r-pill border px-3 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
        (active
          ? 'border-tech/30 bg-tech-tint/80 text-tech-fg'
          : 'border-border bg-surface/40 text-ink-muted hover:text-ink hover:border-border-strong')
      }
    >
      {children}
    </button>
  );
}

function MaterielCard({
  m, onClick, clickable,
}: {
  m: MaterielCatalogue;
  onClick: () => void;
  clickable: boolean;
}) {
  const Icon = CATEGORIE_ICON[m.categorie];
  return (
    <Card
      className={
        'flex flex-col gap-3 p-4 transition-all ' +
        (clickable ? 'cursor-pointer hover:border-border-strong hover:shadow-soft ' : '') +
        (m.actif ? '' : 'opacity-60')
      }
      onClick={clickable ? onClick : undefined}
    >
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-r-lg border border-tech/25 bg-tech-tint/60 text-tech-fg">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-display text-sm font-semibold text-ink">{m.nom}</h3>
            {!m.actif && <Badge variant="neutral">Inactif</Badge>}
          </div>
          <p className="mt-0.5 text-xs text-ink-subtle">{CATEGORIE_LABEL[m.categorie]}</p>
        </div>
      </div>

      {m.description && (
        <p className="line-clamp-2 text-xs text-ink-muted">{m.description}</p>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-3 text-xs">
        <span className="font-medium text-ink">
          {m.quantiteTotale} {m.uniteLibelle}{m.quantiteTotale > 1 ? 's' : ''}
        </span>
        <span className="flex items-center gap-1 text-ink-muted">
          <Euro className="h-3.5 w-3.5" aria-hidden="true" />
          Caution {formatEuro(m.cautionEuros)} €
        </span>
        {m.categorie === 'salle' ? (
          <span className="text-ink-muted">
            {m.tarifDemiJourneeEuros != null && `½ j : ${formatEuro(m.tarifDemiJourneeEuros)} €`}
            {m.tarifDemiJourneeEuros != null && m.tarifJourneeEuros != null && ' · '}
            {m.tarifJourneeEuros != null && `journée : ${formatEuro(m.tarifJourneeEuros)} €`}
            {m.tarifDemiJourneeEuros == null && m.tarifJourneeEuros == null && 'Tarif à définir'}
          </span>
        ) : (
          <span className={m.tarifEuros === 0 ? 'text-success-fg' : 'text-ink-muted'}>
            {m.tarifEuros === 0 ? 'Prêt gratuit' : `${formatEuro(m.tarifEuros)} €`}
          </span>
        )}
      </div>
    </Card>
  );
}

// =============================================================================
// FORMULAIRE (création / édition)
// =============================================================================

interface MaterielForm {
  nom: string;
  categorie: MaterielCategorie;
  description: string;
  quantiteTotale: string;
  uniteLibelle: string;
  cautionEuros: string;
  tarifEuros: string;
  tarifDemiJourneeEuros: string;
  tarifJourneeEuros: string;
  tarifsDifferenciesActif: boolean;
  tarifDemiJourneeAssoEuros: string;
  tarifJourneeAssoEuros: string;
  tarifDemiJourneeProEuros: string;
  tarifJourneeProEuros: string;
  tarifMaterielPersoEuros: string;
  tarifMaterielAssoEuros: string;
  tarifMaterielProEuros: string;
  horaireMatinDebut: string;
  horaireMatinFin: string;
  horaireApremDebut: string;
  horaireApremFin: string;
  clesRetraitDebut: string;
  clesRetraitFin: string;
  actif: boolean;
  notes: string;
  dureeMaxJours: string;
  cautionNotifEmail: string;
  cautionNotifUserId: string;
  pretNotifEmail: string;
  pretNotifUserId: string;
  instructionsRetrait: string;
  lieuId: string | null;
}

const EMPTY_FORM: MaterielForm = {
  nom: '', categorie: 'mobilier', description: '',
  quantiteTotale: '1', uniteLibelle: 'unité',
  cautionEuros: '0', tarifEuros: '0',
  tarifDemiJourneeEuros: '', tarifJourneeEuros: '',
  tarifsDifferenciesActif: false,
  tarifDemiJourneeAssoEuros: '', tarifJourneeAssoEuros: '',
  tarifDemiJourneeProEuros: '', tarifJourneeProEuros: '',
  tarifMaterielPersoEuros: '', tarifMaterielAssoEuros: '', tarifMaterielProEuros: '',
  horaireMatinDebut: '08:00', horaireMatinFin: '13:00',
  horaireApremDebut: '14:00', horaireApremFin: '18:00',
  clesRetraitDebut: '16:00', clesRetraitFin: '17:00',
  actif: true, notes: '',
  dureeMaxJours: '', cautionNotifEmail: '', cautionNotifUserId: '',
  pretNotifEmail: '', pretNotifUserId: '', instructionsRetrait: '',
  lieuId: null,
};

export function MaterielFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { confirm, dialogElement } = useConfirmDialog();

  const [form, setForm] = useState<MaterielForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notifUsers, setNotifUsers] = useState<
    Array<{ id: string; nom: string; role: string; service: string | null }>
  >([]);

  useEffect(() => {
    void api.materiel.notifUsers().then(setNotifUsers).catch(() => { /* optionnel */ });
  }, []);

  useEffect(() => {
    if (!id) return;
    void api.materiel.get(id).then((m) => {
      setForm({
        nom: m.nom,
        categorie: m.categorie,
        description: m.description ?? '',
        quantiteTotale: String(m.quantiteTotale),
        uniteLibelle: m.uniteLibelle,
        cautionEuros: String(m.cautionEuros),
        tarifEuros: String(m.tarifEuros),
        tarifDemiJourneeEuros: m.tarifDemiJourneeEuros != null ? String(m.tarifDemiJourneeEuros) : '',
        tarifJourneeEuros: m.tarifJourneeEuros != null ? String(m.tarifJourneeEuros) : '',
        tarifsDifferenciesActif: m.tarifsDifferenciesActif ?? false,
        tarifDemiJourneeAssoEuros: m.tarifDemiJourneeAssoEuros != null ? String(m.tarifDemiJourneeAssoEuros) : '',
        tarifJourneeAssoEuros: m.tarifJourneeAssoEuros != null ? String(m.tarifJourneeAssoEuros) : '',
        tarifDemiJourneeProEuros: m.tarifDemiJourneeProEuros != null ? String(m.tarifDemiJourneeProEuros) : '',
        tarifJourneeProEuros: m.tarifJourneeProEuros != null ? String(m.tarifJourneeProEuros) : '',
        tarifMaterielPersoEuros: m.tarifMaterielPersoEuros != null ? String(m.tarifMaterielPersoEuros) : '',
        tarifMaterielAssoEuros: m.tarifMaterielAssoEuros != null ? String(m.tarifMaterielAssoEuros) : '',
        tarifMaterielProEuros: m.tarifMaterielProEuros != null ? String(m.tarifMaterielProEuros) : '',
        horaireMatinDebut: m.horaireMatinDebut ?? '08:00',
        horaireMatinFin: m.horaireMatinFin ?? '13:00',
        horaireApremDebut: m.horaireApremDebut ?? '14:00',
        horaireApremFin: m.horaireApremFin ?? '18:00',
        clesRetraitDebut: m.clesRetraitDebut ?? '16:00',
        clesRetraitFin: m.clesRetraitFin ?? '17:00',
        actif: m.actif,
        notes: m.notes ?? '',
        dureeMaxJours: m.dureeMaxJours != null ? String(m.dureeMaxJours) : '',
        cautionNotifEmail: m.cautionNotifEmail ?? '',
        cautionNotifUserId: m.cautionNotifUserId ?? '',
        pretNotifEmail: m.pretNotifEmail ?? '',
        pretNotifUserId: m.pretNotifUserId ?? '',
        instructionsRetrait: m.instructionsRetrait ?? '',
        lieuId: m.lieuId ?? null,
      });
      setLoading(false);
    }).catch(() => { setError('Matériel introuvable'); setLoading(false); });
  }, [id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.nom.trim() || form.nom.trim().length < 2) {
      setError('Le nom doit comporter au moins 2 caractères.');
      return;
    }
    setSubmitting(true); setError(null);
    const parseTarif = (v: string): number | null => (v.trim() ? parseFloat(v.replace(',', '.')) : null);
    const body = {
      nom: form.nom.trim(),
      categorie: form.categorie,
      quantiteTotale: parseInt(form.quantiteTotale, 10) || 0,
      uniteLibelle: form.uniteLibelle.trim() || 'unité',
      cautionEuros: parseFloat(form.cautionEuros.replace(',', '.')) || 0,
      tarifEuros: parseFloat(form.tarifEuros.replace(',', '.')) || 0,
      // Champs salle : envoyés seulement pour une salle, sinon remis à null.
      tarifDemiJourneeEuros: form.categorie === 'salle'
        ? (form.tarifDemiJourneeEuros.trim() ? parseFloat(form.tarifDemiJourneeEuros.replace(',', '.')) : null) : null,
      tarifJourneeEuros: form.categorie === 'salle'
        ? (form.tarifJourneeEuros.trim() ? parseFloat(form.tarifJourneeEuros.replace(',', '.')) : null) : null,
      horaireMatinDebut: form.categorie === 'salle' ? (form.horaireMatinDebut || null) : null,
      horaireMatinFin: form.categorie === 'salle' ? (form.horaireMatinFin || null) : null,
      horaireApremDebut: form.categorie === 'salle' ? (form.horaireApremDebut || null) : null,
      horaireApremFin: form.categorie === 'salle' ? (form.horaireApremFin || null) : null,
      clesRetraitDebut: form.categorie === 'salle' ? (form.clesRetraitDebut || null) : null,
      clesRetraitFin: form.categorie === 'salle' ? (form.clesRetraitFin || null) : null,
      actif: form.actif,
      notes: form.notes.trim() || undefined,
      dureeMaxJours: form.dureeMaxJours.trim() ? parseInt(form.dureeMaxJours, 10) : null,
      cautionNotifEmail: form.cautionNotifEmail.trim() || null,
      cautionNotifUserId: form.cautionNotifUserId || null,
      pretNotifEmail: form.pretNotifEmail.trim() || null,
      pretNotifUserId: form.pretNotifUserId || null,
      instructionsRetrait: form.instructionsRetrait.trim() || null,
      // S4-b : rattachement à un lieu (surtout pour une salle du référentiel).
      lieuId: form.lieuId,
      // Tarifs différenciés perso/asso/pro (mig. 162) — interrupteur + valeurs.
      tarifsDifferenciesActif: form.tarifsDifferenciesActif,
      tarifDemiJourneeAssoEuros: form.categorie === 'salle' ? parseTarif(form.tarifDemiJourneeAssoEuros) : null,
      tarifJourneeAssoEuros: form.categorie === 'salle' ? parseTarif(form.tarifJourneeAssoEuros) : null,
      tarifDemiJourneeProEuros: form.categorie === 'salle' ? parseTarif(form.tarifDemiJourneeProEuros) : null,
      tarifJourneeProEuros: form.categorie === 'salle' ? parseTarif(form.tarifJourneeProEuros) : null,
      tarifMaterielPersoEuros: form.categorie !== 'salle' ? parseTarif(form.tarifMaterielPersoEuros) : null,
      tarifMaterielAssoEuros: form.categorie !== 'salle' ? parseTarif(form.tarifMaterielAssoEuros) : null,
      tarifMaterielProEuros: form.categorie !== 'salle' ? parseTarif(form.tarifMaterielProEuros) : null,
    };
    try {
      if (isEdit && id) await api.materiel.update(id, body);
      else await api.materiel.create(body);
      navigate('/materiel');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur à l\'enregistrement');
      setSubmitting(false);
    }
  }

  /**
   * Mise à jour de l'état des lieux « vivant » du bien : on ouvre le dernier EDL
   * du lieu rattaché en édition (le manager repasse les points KO→OK après
   * travaux, avec commentaire interne). S'il n'y a pas encore d'EDL, on redirige
   * vers l'onglet États des lieux pour en créer un.
   */
  async function ouvrirEtatDesLieux() {
    if (!id) return;
    try {
      const etat = await api.materiel.etatCourant(id);
      if (!etat.lieuId) {
        await confirm({
          title: 'Aucun lieu rattaché',
          description: 'Rattachez d\u2019abord ce matériel à un lieu (référentiel Lieux) pour gérer son état des lieux.',
          confirmLabel: 'Compris',
        });
        return;
      }
      if (etat.edlId) {
        navigate(`/service-technique/signalements/${etat.edlId}`);
      } else {
        // Pas encore d'état des lieux réalisé pour ce lieu → aller le créer.
        const go = await confirm({
          title: 'Aucun état des lieux pour ce lieu',
          description: 'Aucun état des lieux n\u2019a encore été réalisé. Créez-en un depuis l\u2019onglet « États des lieux ».',
          confirmLabel: 'Aller aux états des lieux',
        });
        if (go) navigate('/service-technique/etats-des-lieux/templates');
      }
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Impossible d\u2019ouvrir l\u2019état des lieux');
    }
  }

  async function handleDelete() {
    if (!id) return;
    const ok = await confirm({
      title: 'Retirer ce matériel ?',
      description: 'Il disparaîtra du catalogue. Cette action peut être faite par sécurité avant les réservations.',
      confirmLabel: 'Retirer du catalogue',
      danger: true,
    });
    if (!ok) return;
    try { await api.materiel.delete(id); navigate('/materiel'); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur'); }
  }

  if (loading) {
    return (
      <MaterielLayout title="Matériel" eyebrow="Location de matériel" icon={Boxes}>
        <p className="py-12 text-center text-sm text-ink-subtle">Chargement…</p>
      </MaterielLayout>
    );
  }

  return (
    <MaterielLayout
      title={isEdit ? 'Modifier le matériel' : 'Nouveau matériel'}
      eyebrow="Location de matériel"
      icon={Boxes}
    >
      {dialogElement}
      <Card className="max-w-2xl p-5">
        {error && (
          <Alert variant="error" icon={AlertCircle} className="mb-4">{error}</Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nom du matériel" htmlFor="mf-nom" required>
              <input
                id="mf-nom"
                type="text"
                value={form.nom}
                onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                placeholder="Chapiteau 3×3 m, Tables (lot de 5)…"
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Catégorie" htmlFor="mf-cat">
              <select
                id="mf-cat"
                value={form.categorie}
                onChange={(e) => setForm((f) => ({ ...f, categorie: e.target.value as MaterielCategorie }))}
                className={INPUT_CLS}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{CATEGORIE_LABEL[c]}</option>
                ))}
              </select>
            </Field>
          </div>

          {form.categorie === 'salle' && (
            <Field
              label="Lieu du référentiel"
              htmlFor="mf-lieu"
              hint="Rattachez cette salle à un bâtiment communal (le référentiel de lieux)."
            >
              <LieuSelect
                value={form.lieuId}
                onChange={(lieuId) => setForm((f) => ({ ...f, lieuId }))}
                className={INPUT_CLS}
              />
            </Field>
          )}
          <Field label="Description" htmlFor="mf-desc" hint="Visible par l'administré lors de la réservation.">
            <textarea
              id="mf-desc"
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Dimensions, état, conditions d'usage…"
              className={TEXTAREA_CLS}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Quantité possédée" htmlFor="mf-qte">
              <input
                id="mf-qte"
                type="number" min={0}
                value={form.quantiteTotale}
                onChange={(e) => setForm((f) => ({ ...f, quantiteTotale: e.target.value }))}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Unité" htmlFor="mf-unite" hint="« unité », « lot »…">
              <input
                id="mf-unite"
                type="text"
                value={form.uniteLibelle}
                onChange={(e) => setForm((f) => ({ ...f, uniteLibelle: e.target.value }))}
                className={INPUT_CLS}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Caution (€)" htmlFor="mf-caution">
              <input
                id="mf-caution"
                type="number" min={0} step="0.01"
                value={form.cautionEuros}
                onChange={(e) => setForm((f) => ({ ...f, cautionEuros: e.target.value }))}
                className={INPUT_CLS}
              />
            </Field>
            {form.categorie !== 'salle' && (
              <Field label="Tarif (€)" htmlFor="mf-tarif" hint="0 = prêt gratuit">
                <input
                  id="mf-tarif"
                  type="number" min={0} step="0.01"
                  value={form.tarifEuros}
                  onChange={(e) => setForm((f) => ({ ...f, tarifEuros: e.target.value }))}
                  className={INPUT_CLS}
                />
              </Field>
            )}
          </div>

          {/* ─────────── Matériel : forfait différencié perso / asso / pro ─────────── */}
          {form.categorie !== 'salle' && (
            <div className="rounded-r-lg border border-border/60 bg-surface/20 p-3.5 space-y-2">
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={form.tarifsDifferenciesActif}
                  onChange={(e) => setForm((f) => ({ ...f, tarifsDifferenciesActif: e.target.checked }))} />
                Forfait de location différencié selon le demandeur (perso / asso / pro)
              </label>
              {form.tarifsDifferenciesActif && (
                <>
                  <p className="text-xs text-ink-subtle">Forfait par unité. Vide = gratuit pour ce public.</p>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Perso (€)" htmlFor="mf-mp">
                      <input id="mf-mp" type="number" min={0} step="0.01" value={form.tarifMaterielPersoEuros}
                        onChange={(e) => setForm((f) => ({ ...f, tarifMaterielPersoEuros: e.target.value }))} className={INPUT_CLS} />
                    </Field>
                    <Field label="Assoc. (€)" htmlFor="mf-ma">
                      <input id="mf-ma" type="number" min={0} step="0.01" value={form.tarifMaterielAssoEuros}
                        onChange={(e) => setForm((f) => ({ ...f, tarifMaterielAssoEuros: e.target.value }))} className={INPUT_CLS} />
                    </Field>
                    <Field label="Pro (€)" htmlFor="mf-mpro">
                      <input id="mf-mpro" type="number" min={0} step="0.01" value={form.tarifMaterielProEuros}
                        onChange={(e) => setForm((f) => ({ ...f, tarifMaterielProEuros: e.target.value }))} className={INPUT_CLS} />
                    </Field>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ─────────── Spécifique SALLE : tarifs demi-journée / journée + horaires ─────────── */}
          {form.categorie === 'salle' && (
            <div className="rounded-r-lg border border-tech/40 bg-tech/5 p-3.5 space-y-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-ink">
                <DoorOpen className="h-3.5 w-3.5 text-tech" /> Tarification de la salle
              </p>
              <p className="text-xs text-ink-subtle">
                Une salle se loue à la <strong>demi-journée</strong> ou à la <strong>journée</strong> (pas au nombre de jours).
                Le tarif est unitaire : si l'administré réserve 2 journées, il paie 2× le tarif journée.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tarif demi-journée (€)" htmlFor="mf-tarif-demi">
                  <input id="mf-tarif-demi" type="number" min={0} step="0.01"
                    value={form.tarifDemiJourneeEuros}
                    onChange={(e) => setForm((f) => ({ ...f, tarifDemiJourneeEuros: e.target.value }))}
                    className={INPUT_CLS} placeholder="ex. 150" />
                </Field>
                <Field label="Tarif journée (€)" htmlFor="mf-tarif-jour">
                  <input id="mf-tarif-jour" type="number" min={0} step="0.01"
                    value={form.tarifJourneeEuros}
                    onChange={(e) => setForm((f) => ({ ...f, tarifJourneeEuros: e.target.value }))}
                    className={INPUT_CLS} placeholder="ex. 250" />
                </Field>
              </div>

              {/* Tarifs différenciés perso / asso / pro (optionnel). */}
              <label className="mt-2 flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={form.tarifsDifferenciesActif}
                  onChange={(e) => setForm((f) => ({ ...f, tarifsDifferenciesActif: e.target.checked }))} />
                Appliquer des tarifs différents selon le demandeur (associations / professionnels)
              </label>
              {form.tarifsDifferenciesActif && (
                <div className="rounded-r-lg border border-border/60 bg-surface/20 p-3">
                  <p className="mb-2 text-xs text-ink-subtle">
                    Laissez vide pour appliquer le tarif de base ci-dessus. Le tarif « perso » reste celui de base.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Assoc. — demi-journée (€)" htmlFor="mf-ta-demi">
                      <input id="mf-ta-demi" type="number" min={0} step="0.01" value={form.tarifDemiJourneeAssoEuros}
                        onChange={(e) => setForm((f) => ({ ...f, tarifDemiJourneeAssoEuros: e.target.value }))}
                        className={INPUT_CLS} placeholder="ex. 80" />
                    </Field>
                    <Field label="Assoc. — journée (€)" htmlFor="mf-ta-jour">
                      <input id="mf-ta-jour" type="number" min={0} step="0.01" value={form.tarifJourneeAssoEuros}
                        onChange={(e) => setForm((f) => ({ ...f, tarifJourneeAssoEuros: e.target.value }))}
                        className={INPUT_CLS} placeholder="ex. 130" />
                    </Field>
                    <Field label="Pro — demi-journée (€)" htmlFor="mf-tp-demi">
                      <input id="mf-tp-demi" type="number" min={0} step="0.01" value={form.tarifDemiJourneeProEuros}
                        onChange={(e) => setForm((f) => ({ ...f, tarifDemiJourneeProEuros: e.target.value }))}
                        className={INPUT_CLS} placeholder="ex. 200" />
                    </Field>
                    <Field label="Pro — journée (€)" htmlFor="mf-tp-jour">
                      <input id="mf-tp-jour" type="number" min={0} step="0.01" value={form.tarifJourneeProEuros}
                        onChange={(e) => setForm((f) => ({ ...f, tarifJourneeProEuros: e.target.value }))}
                        className={INPUT_CLS} placeholder="ex. 320" />
                    </Field>
                  </div>
                </div>
              )}
              <p className="text-xs font-medium text-ink pt-1">Créneaux horaires (modifiables)</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Matin — début" htmlFor="mf-hmd">
                  <input id="mf-hmd" type="time" value={form.horaireMatinDebut}
                    onChange={(e) => setForm((f) => ({ ...f, horaireMatinDebut: e.target.value }))} className={INPUT_CLS} />
                </Field>
                <Field label="Matin — fin" htmlFor="mf-hmf">
                  <input id="mf-hmf" type="time" value={form.horaireMatinFin}
                    onChange={(e) => setForm((f) => ({ ...f, horaireMatinFin: e.target.value }))} className={INPUT_CLS} />
                </Field>
                <Field label="Après-midi — début" htmlFor="mf-had">
                  <input id="mf-had" type="time" value={form.horaireApremDebut}
                    onChange={(e) => setForm((f) => ({ ...f, horaireApremDebut: e.target.value }))} className={INPUT_CLS} />
                </Field>
                <Field label="Après-midi — fin" htmlFor="mf-haf">
                  <input id="mf-haf" type="time" value={form.horaireApremFin}
                    onChange={(e) => setForm((f) => ({ ...f, horaireApremFin: e.target.value }))} className={INPUT_CLS} />
                </Field>
              </div>
              <p className="text-xs font-medium text-ink pt-1">Retrait des clés (la veille)</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Clés — début" htmlFor="mf-kd">
                  <input id="mf-kd" type="time" value={form.clesRetraitDebut}
                    onChange={(e) => setForm((f) => ({ ...f, clesRetraitDebut: e.target.value }))} className={INPUT_CLS} />
                </Field>
                <Field label="Clés — fin" htmlFor="mf-kf">
                  <input id="mf-kf" type="time" value={form.clesRetraitFin}
                    onChange={(e) => setForm((f) => ({ ...f, clesRetraitFin: e.target.value }))} className={INPUT_CLS} />
                </Field>
              </div>
              <p className="text-xs text-ink-subtle">
                Les clés se récupèrent la veille du créneau, sur cette plage (la mairie ferme ensuite). Défaut : 16:00 → 17:00.
              </p>
              <p className="text-xs text-ink-subtle">
                Astuce : laissez un battement entre la fin du matin et le début de l'après-midi (ex. 13:00 → 14:00) pour le nettoyage.
              </p>
            </div>
          )}

          <label className="flex items-center gap-2.5 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.actif}
              onChange={(e) => setForm((f) => ({ ...f, actif: e.target.checked }))}
              className="h-4 w-4 rounded border-border text-tech focus:ring-2 focus:ring-tech"
            />
            Visible au catalogue (matériel actif)
          </label>

          <Field
            label="Durée max de prêt (jours)" htmlFor="mf-duree"
            hint="Vide = pas de limite. L'administré ne pourra pas réserver au-delà."
          >
            <input
              id="mf-duree" type="number" min={1} max={365}
              value={form.dureeMaxJours}
              onChange={(e) => setForm((f) => ({ ...f, dureeMaxJours: e.target.value }))}
              className={INPUT_CLS}
              placeholder="ex. 7"
            />
          </Field>

          <div className="rounded-r-lg border border-border bg-surface/30 p-3.5">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-ink">
              <Euro className="h-3.5 w-3.5 text-tech" aria-hidden="true" />
              Relance « caution à rendre »
            </p>
            <p className="mb-3 mt-1 text-xs text-ink-muted">
              Au retour du matériel, ces destinataires sont prévenus que la caution
              peut être restituée. Les deux champs sont optionnels.
            </p>
            <div className="space-y-3">
              <Field label="Email à prévenir" htmlFor="mf-caution-email" hint="Ex. le secrétariat.">
                <input
                  id="mf-caution-email" type="email"
                  value={form.cautionNotifEmail}
                  onChange={(e) => setForm((f) => ({ ...f, cautionNotifEmail: e.target.value }))}
                  className={INPUT_CLS}
                  placeholder="secretariat@mairie.fr"
                />
              </Field>
              <Field label="Agent à prévenir (cloche)" htmlFor="mf-caution-user" hint="Notification interne.">
                <select
                  id="mf-caution-user"
                  value={form.cautionNotifUserId}
                  onChange={(e) => setForm((f) => ({ ...f, cautionNotifUserId: e.target.value }))}
                  className={INPUT_CLS}
                >
                  <option value="">— Aucun —</option>
                  {notifUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.nom} ({u.role})</option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div className="rounded-r-lg border border-border bg-surface/30 p-3.5">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-ink">
              <Inbox className="h-3.5 w-3.5 text-tech" aria-hidden="true" />
              Relance « demande de prêt »
            </p>
            <p className="mb-3 mt-1 text-xs text-ink-muted">
              À la réception d'une demande pour ce matériel, ces destinataires sont
              prévenus qu'un prêt est à préparer. Les deux champs sont optionnels.
            </p>
            <div className="space-y-3">
              <Field label="Email à prévenir" htmlFor="mf-pret-email" hint="Ex. l'agent technique.">
                <input
                  id="mf-pret-email" type="email"
                  value={form.pretNotifEmail}
                  onChange={(e) => setForm((f) => ({ ...f, pretNotifEmail: e.target.value }))}
                  className={INPUT_CLS}
                  placeholder="atelier@mairie.fr"
                />
              </Field>
              <Field label="Agent à prévenir (cloche)" htmlFor="mf-pret-user" hint="Notification interne.">
                <select
                  id="mf-pret-user"
                  value={form.pretNotifUserId}
                  onChange={(e) => setForm((f) => ({ ...f, pretNotifUserId: e.target.value }))}
                  className={INPUT_CLS}
                >
                  <option value="">— Aucun —</option>
                  {notifUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.nom} ({u.role})</option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <Field
            label="Instructions de retrait" htmlFor="mf-instructions"
            hint="Inséré dans le mail de validation envoyé à l'administré (lieu, horaires)."
          >
            <textarea
              id="mf-instructions" rows={3}
              value={form.instructionsRetrait}
              onChange={(e) => setForm((f) => ({ ...f, instructionsRetrait: e.target.value }))}
              className={TEXTAREA_CLS}
              placeholder="Ex. Retrait au dépôt municipal, 12 rue des Ateliers, du lundi au vendredi 8h-12h."
            />
          </Field>

          <Field label="Notes internes" htmlFor="mf-notes" hint="Non visible par les administrés.">
            <textarea
              id="mf-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className={TEXTAREA_CLS}
            />
          </Field>

          {/* Photos de la fiche (2 max, internes) — item 3.10. Disponible une
              fois la fiche créée : l'upload a besoin de son id. */}
          {isEdit && id && (
            <FichePhotos
              ficheId={id}
              api={api.materiel.photos}
              titre="Photos du matériel / de la salle"
            />
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
            <Button type="submit" variant="primary" size="sm" disabled={submitting}>
              <Save className="h-4 w-4" aria-hidden="true" />
              {submitting ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer le matériel'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/materiel')}>
              Annuler
            </Button>
            {isEdit && (
              <Button type="button" variant="secondary" size="sm" onClick={() => void ouvrirEtatDesLieux()}>
                <ClipboardList className="h-4 w-4" aria-hidden="true" />
                Mettre à jour l'état des lieux
              </Button>
            )}
            {isEdit && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto text-urgent-fg"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Retirer
              </Button>
            )}
          </div>
        </form>
      </Card>
    </MaterielLayout>
  );
}

// =============================================================================
// SectionPill — bascule Catalogue / Réservations
// =============================================================================

function SectionPill({
  children, active, onClick,
}: { children: React.ReactNode; active?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex h-9 items-center gap-1.5 rounded-r-pill border px-3.5 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
        (active
          ? 'border-tech/30 bg-tech-tint/80 text-tech-fg'
          : 'border-border bg-surface/40 text-ink-muted hover:text-ink hover:border-border-strong')
      }
    >
      {children}
    </button>
  );
}

// =============================================================================
// RÉSERVATIONS — backoffice staff
// =============================================================================

function formatResaDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}

type ResaBucket = 'toutes' | 'demande' | 'validee' | 'remise' | 'retournee' | 'closes';

const RESA_BUCKETS: { key: ResaBucket; label: string }[] = [
  { key: 'toutes',    label: 'Toutes' },
  { key: 'demande',   label: 'À traiter' },
  { key: 'validee',   label: 'Validées' },
  { key: 'remise',    label: 'Remises' },
  { key: 'retournee', label: 'Terminées' },
  { key: 'closes',    label: 'Refusées / annulées' },
];

export function MaterielReservationsPage() {
  const navigate = useNavigate();
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<MaterielReservation[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<ResaBucket>('toutes');
  const [busyId, setBusyId] = useState<string | null>(null);
  // 9.2 — quand un agent technique ouvre lui-même la salle, c'est lui qui donne
  // le trousseau. Il coche ici, et la GRC verra la remise déjà faite.
  const [clesChoisies, setClesChoisies] = useState<Record<string, string[]>>({});

  async function reload() {
    setLoading(true); setError(null);
    try { setItems(await api.materiel.reservations.list()); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void reload(); }, []);

  async function controlerEtat(r: MaterielReservation, conforme: boolean) {
    const note = await prompt({
      title: `Contrôle d'état — ${r.numero}`,
      description: conforme
        ? `Salle déclarée CONFORME. Vous pouvez ajouter une remarque (optionnel). La GRC pourra ensuite récupérer les clés et rendre la caution.`
        : `Salle déclarée NON CONFORME. Décrivez le problème (porte abîmée, ménage non fait, devis à prévoir…) — ce commentaire sera visible par la GRC et figurera sur le récapitulatif.`,
      confirmLabel: conforme ? 'Valider — conforme' : 'Valider — non conforme',
    });
    if (note === null) return;
    if (!conforme && !note.trim()) { setError('Pour un état non conforme, un commentaire est requis.'); return; }
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.controleEtat(r.id, { conforme, note: note.trim() || undefined });
      await reload();

      // ── Tranche 2 de 9.2 — l'état des lieux de sortie, proposé ────────────
      //
      // PROPOSÉ, pas imposé (décision Henrico 31/07) : un agent qui vient de
      // constater une salle impeccable ne doit pas être retenu par un formulaire.
      // Mais c'est cet état des lieux qui deviendra l'état courant du lieu, donc
      // celui que le locataire SUIVANT recevra — la boucle se referme là, et
      // c'est le seul moment où l'agent a la salle en tête.
      if (r.creneau != null) {
        const ouvrir = await confirm({
          title: 'Faire l’état des lieux de sortie ?',
          description: conforme
            ? 'Il deviendra l’état de référence pour la prochaine location de cette salle.'
            : 'Recommandé : il consigne les écarts constatés et servira de référence pour la caution et la prochaine location.',
          confirmLabel: 'Ouvrir l’état des lieux',
        });
        if (ouvrir) {
          try {
            const etat = await api.materiel.etatCourant(r.materielId);
            if (etat.edlId) navigate(`/service-technique/signalements/${etat.edlId}`);
            else navigate('/service-technique/etats-des-lieux/templates');
          } catch {
            navigate('/service-technique/etats-des-lieux/templates');
          }
        }
      }
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }
  // Permet de modifier l'observation plus tard (litige/devis), même après le retour.
  async function modifierObservation(r: MaterielReservation) {
    const note = await prompt({
      title: `Observation — ${r.numero}`,
      description: 'Mettez à jour le suivi (ex. « devis 350 € reçu », « caution à conserver »). Visible par la GRC et sur le récapitulatif.',
      defaultValue: r.controleEtatNote ?? '',
      confirmLabel: 'Enregistrer',
    });
    if (note === null) return;
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.controleEtat(r.id, { conforme: r.controleEtatConforme !== false, note: note.trim() || undefined });
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }

  async function changeStatut(r: MaterielReservation, statut: MaterielReservationStatut) {
    setError(null);
    let motifRefus: string | undefined;
    if (statut === 'refusee') {
      const m = await prompt({
        title: `Refuser la réservation ${r.numero} ?`,
        description: 'Indiquez le motif du refus — il sera communiqué à l\'administré.',
        confirmLabel: 'Refuser la réservation',
      });
      if (m === null) return;
      if (!m.trim()) { setError('Le motif de refus est obligatoire.'); return; }
      motifRefus = m.trim();
    } else {
      const labels: Record<string, { t: string; d: string; c: string }> = {
        validee:   { t: 'Valider cette réservation ?', d: `${r.numero} — ${r.materielNom}.`, c: 'Valider' },
        remise:    { t: 'Confirmer la remise du matériel ?', d: 'À faire au moment du retrait par l\'administré.', c: 'Matériel remis' },
        retournee: { t: 'Confirmer le retour du matériel ?', d: 'La caution peut alors être restituée.', c: 'Matériel retourné' },
      };
      const cfg = labels[statut];
      const ok = await confirm({
        title: cfg?.t ?? 'Confirmer ?',
        description: cfg?.d ?? '',
        confirmLabel: cfg?.c ?? 'Confirmer',
      });
      if (!ok) return;
    }
    setBusyId(r.id);
    try {
      await api.materiel.reservations.updateStatut(r.id, {
        statut,
        ...(motifRefus ? { motifRefus } : {}),
        // 9.2 — n'a de sens qu'à la remise ; le serveur l'ignore ailleurs.
        ...(statut === 'remise' ? { cleIds: clesChoisies[r.id] ?? [] } : {}),
      });
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }

  const filtered = (items ?? []).filter((r) => {
    if (bucket === 'toutes') return true;
    if (bucket === 'closes') return r.statut === 'refusee' || r.statut === 'annulee';
    return r.statut === bucket;
  });

  const nbATraiter = (items ?? []).filter((r) => r.statut === 'demande').length;

  return (
    <MaterielLayout
      title="Réservations"
      eyebrow="Location de matériel"
      icon={ClipboardList}
      section="reservations"
    >
      {dialogElement}
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      <div className="mb-4 flex flex-wrap gap-2">
        {RESA_BUCKETS.map((b) => (
          <CatFilterPill key={b.key} active={bucket === b.key} onClick={() => setBucket(b.key)}>
            {b.label}
            {b.key === 'demande' && nbATraiter > 0 && (
              <span className="ml-1.5 rounded-r-pill bg-warning-tint px-1.5 text-warning">{nbATraiter}</span>
            )}
          </CatFilterPill>
        ))}
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-ink-subtle">Chargement…</p>
      ) : filtered.length === 0 ? (
        <Card className="py-12 text-center">
          <ClipboardList className="mx-auto h-10 w-10 text-ink-dim" aria-hidden="true" />
          <p className="mt-3 text-sm text-ink-muted">
            {items && items.length === 0
              ? 'Aucune réservation pour le moment.'
              : 'Aucune réservation dans ce filtre.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const st = RESERVATION_STATUT[r.statut];
            const busy = busyId === r.id;
            return (
              <Card key={r.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-sm font-semibold text-ink">{r.materielNom}</h3>
                      <Badge variant={st.variant}>{st.label}</Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-ink-subtle">{r.numero}</p>
                  </div>
                  <span className="text-xs font-medium text-ink">
                    {r.quantite} unité{r.quantite > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-1.5 border-t border-border pt-3 text-xs text-ink-muted sm:grid-cols-2">
                  <span className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {r.demandeurNom ?? 'Administré'}
                    {r.demandeurTelephone && <span className="text-ink-subtle">· {r.demandeurTelephone}</span>}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {formatResaDate(r.dateRetrait)} → {formatResaDate(r.dateRetour)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Euro className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    Caution {formatEuro(r.cautionEuros)} €
                  </span>
                  {r.demandeurEmail && (
                    <span className="truncate text-ink-subtle">{r.demandeurEmail}</span>
                  )}
                </div>

                {r.statut === 'refusee' && r.motifRefus && (
                  <p className="mt-2 rounded-r-lg bg-urgent-tint/50 p-2 text-xs text-urgent">
                    Motif : {r.motifRefus}
                  </p>
                )}

                {(r.statut === 'demande' || r.statut === 'validee' || r.statut === 'remise' || (r.creneau != null && r.statut === 'retournee')) && (
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
                    {r.statut === 'demande' && (
                      <div className="w-full space-y-3">
                        {/* 9.1 — ce que l'organisateur a annoncé. Affiché AVANT
                            l'état des lieux : c'est ce qui décide s'il faut un
                            arrêté, donc ce qui conditionne le délai. */}
                        <DeclarationManifestation
                          reservation={r}
                          onGenererArrete={(id) =>
                            navigate(`/actes/nouveau?sourceType=reservation&sourceId=${id}`)}
                        />
                        {/* Tranche 2 de 9.2 — valider une salle, c'est attester
                            qu'elle est prêtable. On montre donc l'état des lieux
                            au lieu de le faire chercher ailleurs. */}
                        {r.creneau != null && (
                          <EtatDesLieuxResume
                            materielId={r.materielId}
                            aide="C’est ce document que le locataire recevra, et la référence au retour."
                            onOuvrir={(etat) => {
                              if (etat.edlId) navigate(`/service-technique/signalements/${etat.edlId}`);
                              else navigate('/service-technique/etats-des-lieux/templates');
                            }}
                          />
                        )}
                        <div className="flex flex-wrap gap-2">
                          <Button variant="primary" size="sm" disabled={busy}
                            onClick={() => changeStatut(r, 'validee')}>
                            <Check className="h-4 w-4" aria-hidden="true" />
                            {r.creneau != null ? 'Valider — état des lieux à jour' : 'Valider'}
                          </Button>
                          <Button variant="ghost" size="sm" className="text-urgent-fg" disabled={busy}
                            onClick={() => changeStatut(r, 'refusee')}>
                            <X className="h-4 w-4" aria-hidden="true" /> Refuser
                          </Button>
                        </div>
                      </div>
                    )}
                    {/* Salle : la remise et le retour des clés sont ouverts à la
                        GRC ET au Service Technique (retour mairie 31/07) — le
                        premier qui enregistre fait foi. Le contrôle d'état, lui,
                        reste au Service Technique seul. */}
                    {r.creneau != null ? (
                      r.statut === 'validee' ? (
                        <div className="w-full space-y-3">
                          <p className="text-xs text-ink-subtle">
                            Les clés sont normalement remises à l’accueil par la GRC. Si vous
                            ouvrez la salle vous-même, enregistrez la remise ici.
                          </p>
                          <ClesRemiseSelect
                            reservationId={r.id}
                            aide="Cochez le ou les exemplaires que vous donnez en main propre."
                            onChange={(ids) => setClesChoisies((m) => ({ ...m, [r.id]: ids }))}
                          />
                          <div className="flex justify-end">
                            <Button variant="secondary" size="sm" disabled={busy}
                              onClick={() => changeStatut(r, 'remise')}>
                              <KeyRound className="h-4 w-4" aria-hidden="true" /> Clés remises en main propre
                            </Button>
                          </div>
                        </div>
                      ) : (r.statut === 'remise' || r.statut === 'retournee') ? (
                        r.controleEtatOk ? (
                          <div className="w-full space-y-1.5">
                            <p className={`flex items-center gap-1.5 text-xs font-medium ${r.controleEtatConforme === false ? 'text-urgent-fg' : 'text-success-fg'}`}>
                              <Check className="h-4 w-4" aria-hidden="true" />
                              {r.controleEtatConforme === false ? 'État NON CONFORME' : 'État conforme'}
                              {r.controleEtatParNom ? ` — ${r.controleEtatParNom}` : ''}
                            </p>
                            {r.controleEtatNote && (
                              <p className="rounded-r-md bg-surface/60 p-2 text-xs text-ink-muted">Observation : {r.controleEtatNote}</p>
                            )}
                            <Button variant="ghost" size="sm" disabled={busy} onClick={() => modifierObservation(r)}>
                              Modifier l'observation
                            </Button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            <Button variant="primary" size="sm" disabled={busy} onClick={() => controlerEtat(r, true)}>
                              <Check className="h-4 w-4" aria-hidden="true" /> Conforme
                            </Button>
                            <Button variant="ghost" size="sm" className="text-urgent-fg" disabled={busy} onClick={() => controlerEtat(r, false)}>
                              <X className="h-4 w-4" aria-hidden="true" /> Non conforme
                            </Button>
                          </div>
                        )
                      ) : null
                    ) : (
                      <>
                        {r.statut === 'validee' && (
                          <Button variant="primary" size="sm" disabled={busy}
                            onClick={() => changeStatut(r, 'remise')}>
                            <Check className="h-4 w-4" aria-hidden="true" /> Marquer remis
                          </Button>
                        )}
                        {r.statut === 'remise' && (
                          <Button variant="primary" size="sm" disabled={busy}
                            onClick={() => changeStatut(r, 'retournee')}>
                            <Check className="h-4 w-4" aria-hidden="true" /> Marquer retourné
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </MaterielLayout>
  );
}
